import {
  fetchUserAttributes,
  getCurrentUser,
  fetchAuthSession,
} from "@aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import { useEffect, useRef, useState } from "react";
import {
  createOrganizationCPs,
  deleteOrganizationCPs,
  updateOrganizationCPs,
} from "../graphql/mutations.js";
import {
  listGeneralInformations,
  listOrganizationCPs,
  listOrganizationInformations,
} from "../graphql/queries.js";
import { postEmailNotification } from "../utils/postEmailNotification.js";

const client = generateClient();

// Reuse existing invitation lambda to ask backend to set the user's Cognito custom attribute.
async function setCognitoOrgIdViaInviteLambda(email, orgId) {
  const url =
    "https://kpwdaqkfpck6zvyr67a3eatm6i0ywoup.lambda-url.us-east-1.on.aws/";
  try {
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "setOrganizationId",
        email,
        organizationId: orgId,
      }),
    });
  } catch (err) {
    console.error("Failed to request setting Cognito organizationId:", err);
  }
}

// Request the invite lambda to add the user to the specified Cognito groups.
async function addUserToGroupsViaInviteLambda(email, groups = []) {
  if (!email || !groups || groups.length === 0) return;
  const url =
    "https://kdbbptgaw3jz222sq46ywyfs4u0tjayt.lambda-url.us-east-1.on.aws/";
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "addUserToGroups",
        email,
        groups,
      }),
    });
    if (response.ok) {
      console.log(
        "User added to Professional group. Refreshing session claims...",
      );
      await fetchAuthSession({ forceRefresh: true });
      console.log(
        "Session claims updated successfully. No sign-outs will happen.",
      );
    }
  } catch (err) {
    console.error("Failed to request adding user to Cognito groups:", err);
  }
}

// Request the invite lambda to remove the user from the specified Cognito groups.
async function removeUserFromGroupsViaInviteLambda(email, groups = []) {
  if (!email || !groups || groups.length === 0) return;
  const url =
    "https://kdbbptgaw3jz222sq46ywyfs4u0tjayt.lambda-url.us-east-1.on.aws/";
  try {
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "removeUserFromGroups",
        email,
        groups,
      }),
    });
  } catch (err) {
    console.error("Failed to request removing user from Cognito groups:", err);
  }
}

export default function OrganizationEmployeesTable() {
  const [rows, setRows] = useState([]);
  const [organizationId, setOrganizationId] = useState("");
  const pasteTargetIndex = useRef(0);
  const [saveMessage, setSaveMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isCurrentUserPrimaryAdmin, setIsCurrentUserPrimaryAdmin] =
    useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const containerStyle = {
    padding: 24,
    background: "#fff",
    borderRadius: 10,
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    overflowX: "auto",
    width: isMobile ? "100%" : 1000,
  };

  const actionsRowStyle = {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    marginTop: 8,
  };

  const refreshEmployees = async () => {
    setIsRefreshing(true);
    try {
      await getCurrentUser();
      const attrs = await fetchUserAttributes();
      const accountType = attrs["custom:accountType"];
      const userEmail = attrs.email?.toLowerCase();
      const userSub = attrs.sub;

      let orgId = "";

      if (accountType === "Professional" && userEmail) {
        const cpRes = await client.graphql({
          query: listOrganizationCPs,
          variables: {
            filter: {
              OrganizationCPEmail: { eq: userEmail },
            },
          },
        });

        const cpItems = cpRes.data?.listOrganizationCPs?.items || [];

        const matchingRecord = cpItems.find(
          (item) => item.OrganizationCPEmail?.toLowerCase() === userEmail,
        );

        if (matchingRecord) {
          orgId = matchingRecord.OrganizationId;
          setIsCurrentUserPrimaryAdmin(
            !!matchingRecord.PrimaryOrganizationAdministrator,
          );
        }

        if (!orgId) {
          setSaveMessage("❌ Could not find organization for this user.");
          setIsRefreshing(false);
          return;
        }
      } else {
        // For organization accounts, just use the sub as OrganizationId (your original working logic)
        orgId = userSub;
        setIsCurrentUserPrimaryAdmin(true); // ✅ Org accounts should always have full admin access
      }
      setOrganizationId(orgId);

      // Load CPs for this organization
      const res = await client.graphql({
        query: listOrganizationCPs,
        variables: {
          filter: {
            OrganizationId: { eq: orgId },
          },
        },
      });

      const cpItems = res.data?.listOrganizationCPs?.items || [];
      const emails = Array.from(
        new Set(
          cpItems
            .map((item) => item.OrganizationCPEmail?.trim().toLowerCase())
            .filter(Boolean),
        ),
      );

      let generalInfoByEmail = {};
      if (emails.length > 0) {
        const giRes = await client.graphql({
          query: listGeneralInformations,
          variables: {
            filter: {
              or: emails.map((email) => ({ Email: { eq: email } })),
            },
            limit: 1000,
          },
        });

        const giItems = giRes.data?.listGeneralInformations?.items || [];
        generalInfoByEmail = giItems.reduce((acc, item) => {
          const email = item?.Email?.trim().toLowerCase();
          if (email && !acc[email]) acc[email] = item;
          return acc;
        }, {});
      }

      const existingRows = cpItems.map((item) => {
        const email = item.OrganizationCPEmail?.trim().toLowerCase() || "";
        const gi = generalInfoByEmail[email];

        return {
          email: item.OrganizationCPEmail || "",
          firstName: gi?.FirstName || item.OrganizationCPFirstName || "",
          lastName: gi?.LastName || item.OrganizationCPLastName || "",
          role: gi?.ProfessionalRole || item.OrganizationCPRole || "",
          department: gi?.Department || item.primaryDepartment || "",
          program: gi?.ProgramsUnit || item.primaryProgramsUnit || "",
          service: gi?.Service || item.primaryService || "",
          admin: item.OrganizationAdministrator || false,
          primaryAdmin: item.PrimaryOrganizationAdministrator || false,
          id: item.id,
        };
      });

      const blankRows = Array(3)
        .fill()
        .map(() => ({
          email: "",
          firstName: "",
          lastName: "",
          role: "",
          department: "",
          program: "",
          service: "",
          admin: false,
          primaryAdmin: false,
        }));

      setRows(existingRows.length > 0 ? existingRows : blankRows);
      setSaveMessage("✅ Data refreshed successfully.");
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (err) {
      console.error("Error loading employees:", err);
      setSaveMessage("❌ Failed to load employees.");
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    refreshEmployees();
  }, []);

  const handleDeleteRow = async (index) => {
    const rowToDelete = rows[index];

    if (
      !window.confirm(
        `Remove ${rowToDelete.email || "this employee"} from this organization?`,
      )
    ) {
      return;
    }

    if (rowToDelete.id) {
      try {
        await client.graphql({
          query: deleteOrganizationCPs,
          variables: { input: { id: rowToDelete.id } },
        });
      } catch (err) {
        console.error("Delete failed:", err);
        alert("❌ Failed to delete record.");
        return;
      }

      if (rowToDelete.admin || rowToDelete.primaryAdmin) {
        await removeUserFromGroupsViaInviteLambda(rowToDelete.email, [
          "OrganizationAdmin",
        ]);
      }
    }

    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    const updated = [...rows];
    updated[index][field] =
      field === "admin" || field === "primaryAdmin"
        ? value.target.checked
        : value.target.value;
    setRows(updated);
  };

  const handlePaste = async (e) => {
    const clipboard = e.clipboardData.getData("text");
    const lines = clipboard
      .trim()
      .split("\n")
      .map((line) => line.split("\t"));

    const pastedRows = await Promise.all(
      lines.map(async (cols) => {
        const email = cols[0] || "";
        let generalInfo = null;

        try {
          const res = await client.graphql({
            query: listGeneralInformations,
            variables: {
              filter: { Email: { eq: email } },
            },
          });
          generalInfo = res.data.listGeneralInformations.items[0];
        } catch (err) {
          console.warn("Lookup failed for", email, err);
        }

        return {
          email,
          firstName: generalInfo?.FirstName || cols[3] || "",
          lastName: generalInfo?.LastName || cols[4] || "",
          role: generalInfo?.ProfessionalRole || cols[5] || "",
          department: generalInfo?.Department || cols[6] || "",
          program: generalInfo?.ProgramsUnit || cols[7] || "",
          service: generalInfo?.Service || cols[8] || "",
          admin: cols[1]?.toLowerCase() === "true",
          primaryAdmin: cols[2]?.toLowerCase() === "true",
        };
      }),
    );

    setRows((prev) => {
      const updated = [...prev];
      pastedRows.forEach((row, i) => {
        const idx = pasteTargetIndex.current + i;
        if (idx < updated.length) {
          updated[idx] = { ...updated[idx], ...row };
        } else {
          updated.push(row);
        }
      });
      return updated;
    });

    e.preventDefault();
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        email: "",
        firstName: "",
        lastName: "",
        role: "",
        department: "",
        program: "",
        service: "",
        admin: false,
        primaryAdmin: false,
      },
    ]);
  };

  const saveAll = async () => {
    if (!organizationId) return alert("Missing organization ID.");
    setLoading(true);
    setSaveMessage("");

    // ✅ Enforce only one primary admin

    try {
      const existing = await client.graphql({
        query: listOrganizationCPs,
        variables: { filter: { OrganizationId: { eq: organizationId } } },
      });

      const existingByEmail = {};
      existing.data.listOrganizationCPs.items.forEach((item) => {
        const normalizedEmail = item.OrganizationCPEmail?.trim().toLowerCase();
        if (normalizedEmail) {
          existingByEmail[normalizedEmail] = item;
        }
      });

      for (const row of rows) {
        try {
          const email = row.email?.trim().toLowerCase();
          if (!email) continue; // skip empty emails
          let businessName = "Your Organization";

          try {
            const orgRes = await client.graphql({
              query: listOrganizationInformations, // or your org query
              variables: { filter: { organizationId: { eq: organizationId } } },
            });
            const orgData = orgRes.data.listOrganizationInformations.items[0];
            if (orgData?.BusinessName) businessName = orgData.BusinessName;
          } catch (err) {
            console.warn("Could not fetch business name:", err);
          }
          const generalInfoRes = await client.graphql({
            query: listGeneralInformations,
            variables: { filter: { Email: { eq: email } } },
          });

          const generalInfo =
            generalInfoRes.data.listGeneralInformations.items[0];

          const input = {
            OrganizationId: organizationId,
            OrganizationCPEmail: email,
            OrganizationCPFirstName: generalInfo?.FirstName || row.firstName,
            OrganizationCPLastName: generalInfo?.LastName || row.lastName,
            OrganizationCPRole: generalInfo?.ProfessionalRole || row.role,
            primaryDepartment: generalInfo?.Department || row.department,
            primaryProgramsUnit: generalInfo?.ProgramsUnit || row.program,
            primaryService: generalInfo?.Service || row.service,
            OrganizationAdministrator: row.admin,
            PrimaryOrganizationAdministrator: row.primaryAdmin,
          };

          const existingRecord = row.id
            ? { id: row.id }
            : existingByEmail[email];
          // existingRecord may only carry {id} when row.id was already known;
          // use the freshly-fetched full record for before/after comparisons.
          const previousCpRecord = existingByEmail[email];

          if (existingRecord?.id) {
            await client.graphql({
              query: updateOrganizationCPs,
              variables: {
                input: { id: existingRecord.id, ...input },
              },
            });
            // Every employee needs the org claim set, regardless of whether
            // they've filled out a GeneralInformation profile yet - the
            // invite lambda no-ops gracefully if the Cognito account doesn't
            // exist yet.
            await setCognitoOrgIdViaInviteLambda(email, organizationId);
            // Also request adding user to appropriate Cognito groups
            try {
              const groups = [];
              if (input.OrganizationCPRole) groups.push("Professionals");
              if (
                input.OrganizationAdministrator ||
                input.PrimaryOrganizationAdministrator
              )
                groups.push("OrganizationAdmin");
              if (groups.length)
                await addUserToGroupsViaInviteLambda(email, groups);

              // If they were previously an admin and no longer are, remove
              // them from the OrganizationAdmin Cognito group.
              const wasAdmin =
                previousCpRecord?.OrganizationAdministrator ||
                previousCpRecord?.PrimaryOrganizationAdministrator;
              const isAdminNow =
                input.OrganizationAdministrator ||
                input.PrimaryOrganizationAdministrator;
              if (wasAdmin && !isAdminNow) {
                await removeUserFromGroupsViaInviteLambda(email, [
                  "OrganizationAdmin",
                ]);
              }
            } catch (err) {
              console.warn("Failed requesting group assignment:", err);
            }
          } else {
            await client.graphql({
              query: createOrganizationCPs,
              variables: { input },
            });

            const existingProfileCount =
              generalInfoRes.data?.listGeneralInformations?.items?.length || 0;
            const isExistingProfile = existingProfileCount > 0;

            // The CP record is already saved at this point; an invite-email
            // failure shouldn't roll this row back into an error state.
            try {
              await postEmailNotification({
                to: email,
                subject: isExistingProfile
                  ? "You were added to an organization on Wholistic Health App®"
                  : "Create account on Wholistic Health App®!",
                body: isExistingProfile
                  ? `You were added to ${businessName} on the Wholistic Health App®. Sign in to review your organization access and keep your profile up to date.`
                  : `You are being invited to create an account on the Wholistic Health App by ${businessName}.`,
                includeButton: true,
                buttonUrl: "https://wholistichealthapp.com/",
                buttonText: isExistingProfile
                  ? "Sign in to view your access"
                  : "Create your account",
              });
            } catch (emailErr) {
              console.error("Failed to send invite email:", email, emailErr);
            }

            // Every employee needs the org claim set, regardless of whether
            // they've filled out a GeneralInformation profile yet - the
            // invite lambda no-ops gracefully if the Cognito account doesn't
            // exist yet.
            await setCognitoOrgIdViaInviteLambda(email, organizationId);

            // Also request adding user to appropriate Cognito groups
            try {
              const groups = [];
              if (input.OrganizationCPRole) groups.push("Professionals");
              if (
                input.OrganizationAdministrator ||
                input.PrimaryOrganizationAdministrator
              )
                groups.push("OrganizationAdmin");
              if (groups.length)
                await addUserToGroupsViaInviteLambda(email, groups);
            } catch (err) {
              console.warn("Failed requesting group assignment:", err);
            }
          }
        } catch (err) {
          console.error("Error saving row:", row.email, err);
        }
      }

      // ✅ Reload the latest saved data
      const refreshed = await client.graphql({
        query: listOrganizationCPs,
        variables: {
          filter: {
            OrganizationId: { eq: organizationId },
          },
        },
      });

      const updatedRows = refreshed.data.listOrganizationCPs.items.map(
        (item) => ({
          email: item.OrganizationCPEmail || "",
          firstName: item.OrganizationCPFirstName || "",
          lastName: item.OrganizationCPLastName || "",
          role: item.OrganizationCPRole || "",
          department: item.primaryDepartment || "",
          program: item.primaryProgramsUnit || "",
          service: item.primaryService || "",
          admin: item.OrganizationAdministrator || false,
          primaryAdmin: item.PrimaryOrganizationAdministrator || false,
          id: item.id,
        }),
      );

      setRows(updatedRows);
      setSaveMessage("✅ Employees saved successfully.");
    } catch (error) {
      console.error("Save failed:", error);
      setSaveMessage("❌ An error occurred while saving employees.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={containerStyle}>
      <h2>Organization Employees</h2>
      <p style={employeeInstructionsStyle}>
        Complete the highlighted Email, Administrator, and Primary Admin fields.
        The remaining fields populate automatically when the employee updates
        their profile. To add multiple employees, paste email addresses into a
        blank Email field.
      </p>

      <div onPaste={handlePaste} style={tableWrapperStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th></th> {/* delete column */}
              <th style={requiredHeaderStyle}>Email</th>
              <th style={requiredHeaderStyle}>Administrator</th>
              <th style={requiredHeaderStyle}>Primary Admin</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Role</th>
              <th>Department</th>
              <th>Program</th>
              <th>Service</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td>
                  <button
                    onClick={() => handleDeleteRow(i)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "red",
                      fontWeight: "bold",
                      fontSize: 18,
                      cursor: "pointer",
                    }}
                    title="Delete row"
                  >
                    ❌
                  </button>
                </td>
                <td style={requiredCellStyle}>
                  <input
                    type="email"
                    value={row.email}
                    onChange={(e) => handleChange(i, "email", e)}
                    onFocus={() => (pasteTargetIndex.current = i)}
                    style={{ ...inputStyle, ...requiredInputStyle }}
                  />
                </td>
                <td style={requiredCellStyle}>
                  <input
                    type="checkbox"
                    checked={row.admin}
                    onChange={(e) => handleChange(i, "admin", e)}
                    disabled={!isCurrentUserPrimaryAdmin}
                  />
                </td>
                <td style={requiredCellStyle}>
                  <input
                    type="checkbox"
                    checked={row.primaryAdmin}
                    onChange={(e) => handleChange(i, "primaryAdmin", e)}
                    disabled={!isCurrentUserPrimaryAdmin}
                  />
                </td>
                <td>
                  <input
                    value={row.firstName}
                    onChange={(e) => handleChange(i, "firstName", e)}
                    style={inputStyle}
                  />
                </td>
                <td>
                  <input
                    value={row.lastName}
                    onChange={(e) => handleChange(i, "lastName", e)}
                    style={inputStyle}
                  />
                </td>
                <td>
                  <input
                    value={row.role}
                    onChange={(e) => handleChange(i, "role", e)}
                    style={inputStyle}
                  />
                </td>
                <td>
                  <input
                    value={row.department}
                    onChange={(e) => handleChange(i, "department", e)}
                    style={inputStyle}
                  />
                </td>
                <td>
                  <input
                    value={row.program}
                    onChange={(e) => handleChange(i, "program", e)}
                    style={inputStyle}
                  />
                </td>
                <td>
                  <input
                    value={row.service}
                    onChange={(e) => handleChange(i, "service", e)}
                    style={inputStyle}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={actionsRowStyle}>
        <button onClick={addRow} style={buttonStyle}>
          + Add Row
        </button>
        <button
          onClick={refreshEmployees}
          style={{ ...buttonStyle, backgroundColor: "#D4B65F" }}
          disabled={isRefreshing}
        >
          {isRefreshing ? "Refreshing..." : "Refresh Data"}
        </button>
        <button
          onClick={saveAll}
          style={{ ...buttonStyle, backgroundColor: "#2F5D50" }}
          disabled={loading}
        >
          {loading ? "Saving..." : "Save New Employees"}
        </button>
      </div>

      {saveMessage && (
        <div
          style={{
            marginTop: 10,
            color: saveMessage.startsWith("✅") ? "green" : "red",
          }}
        >
          {saveMessage}
        </div>
      )}
    </div>
  );
}

const tableWrapperStyle = {
  overflowX: "auto",
  width: "100%",
};

const tableStyle = {
  minWidth: "1800px",
  width: "100%",
  borderCollapse: "collapse",
  marginBottom: 20,
};

const inputStyle = {
  width: "100%",
  minWidth: 140,
  padding: 6,
  border: "1px solid #ccc",
  borderRadius: 4,
  fontSize: 14,
  boxSizing: "border-box",
};

const employeeInstructionsStyle = {
  margin: "0 0 16px",
  padding: "10px 12px",
  background: "#E6F4FF",
  borderLeft: "4px solid #3B9DDD",
};

const requiredHeaderStyle = {
  background: "#E6F4FF",
};

const requiredCellStyle = {
  background: "#F4FAFF",
};

const requiredInputStyle = {
  borderColor: "#3B9DDD",
};

const buttonStyle = {
  width: 220,
  marginRight: 0,
  marginBottom: 0,
  display: "block",
  flex: "0 0 220px",
  padding: "10px 16px",
  fontSize: 14,
  border: "none",
  borderRadius: 5,
  backgroundColor: "#0067B3",
  color: "#fff",
  cursor: "pointer",
};
